import React, { useContext } from "react";
import { MaturaReviewContext } from "./MaturaExamRunner/MaturaReviewContext";
import { MaturaPdfPreviewEnabledContext } from "./MaturaPdfPreview";
import { Link, useLocation } from "react-router-dom";
import {
  formatMaturaOriginalSource,
  getMaturaOriginalTaskPath,
  getMaturaOriginalTaskSource,
  getMaturaOriginalTaskSources,
} from "../matura/original/maturaOriginalTaskSources";
import { getExtendedOriginalForTask, getExtendedOriginalTaskPath } from "../matura/original/extendedOriginalTaskRegistry";
import { getBasicCourseGroupSources } from "../matura/basicCourseMetadata";

export default function MaturaOriginalSourceLinks({ task }) {
  const review = useContext(MaturaReviewContext);
  const sourceLinksEnabled = useContext(MaturaPdfPreviewEnabledContext);
  const location = useLocation();
  const allSources = task?.basicCourseGroup
    ? getBasicCourseGroupSources(task.basicCourseGroup)
      .map(source => getMaturaOriginalTaskSource(source.sheetId, source.taskNo)).filter(Boolean)
    : getMaturaOriginalTaskSources(task?.type);
  const basicContext = Boolean(task?.basicCourseSource) || /^(?:\/demo)?\/matura\/sciezka(?:\/|$)/.test(location.pathname);
  const sources = basicContext ? allSources.filter(source => source.sheetId.startsWith("cke-p-")) : allSources;
  const extended = basicContext ? null : getExtendedOriginalForTask(task);
  const exercisePoints = Number(task?.data?.maxPoints ?? task?.points);
  const isPartialSource = source => exercisePoints > 0 && source.points > exercisePoints;
  const hasFullTaskSource = sources.some(isPartialSource);
  if (!sourceLinksEnabled || (review.interactive && !review.reviewAllowed)) return null;
  if (!sources.length && !extended) return null;

  // Extended tasks can also be present in the full-sheet registry. Their
  // dedicated source page contains the correct extended PDF and scoring key.
  if (extended) {
    return <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px 9px", margin: "7px 0 10px", fontSize: "0.78rem" }}>
      <span style={{ fontWeight: 800, opacity: 0.78 }}>Oryginał zadania:</span>
      <Link className="btn-secondary" style={{ padding: "3px 8px", fontSize: "0.76rem", textDecoration: "none" }}
        to={`${location.pathname.startsWith("/demo/") ? "/demo" : ""}${getExtendedOriginalTaskPath(extended)}`}
        target="_blank" rel="noopener noreferrer" title="Otwórz zadanie źródłowe, arkusz i kryteria CKE w nowej karcie">
        {extended.title} · arkusz i kryteria ↗
      </Link>
    </div>;
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "6px 9px",
        margin: "7px 0 10px",
        fontSize: "0.78rem",
      }}
    >
      <span style={{ fontWeight: 800, opacity: 0.78 }}>
        {hasFullTaskSource ? sources.length === 1 ? "Oryginał całego zadania:" : "Oryginały całych zadań:"
          : sources.length === 1 ? "Oryginał zadania:" : "Oryginały zadania:"}
      </span>
      {sources.map((source) => (
        <Link
          key={`${source.sheetId}-${source.taskNo}`}
          className="btn-secondary"
          style={{ padding: "3px 8px", fontSize: "0.76rem", textDecoration: "none" }}
          to={`${location.pathname.startsWith("/demo/") ? "/demo" : ""}${getMaturaOriginalTaskPath(source)}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Otwórz oryginalne zadanie w nowej karcie"
          aria-label={`${formatMaturaOriginalSource(source)} — otwórz w nowej karcie`}
        >
          {formatMaturaOriginalSource(source)}{isPartialSource(source) ? ` · ${source.points} pkt` : ""} ↗
        </Link>
      ))}
    </div>
  );
}
